import React from 'react';
import { useNavigate } from 'react-router';
import './LoginPromptModal.css';

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  action?: 'play' | 'queue' | 'download' | 'favorite' | 'shownotes';
  streamName?: string;
}

const LoginPromptModal: React.FC<LoginPromptModalProps> = ({
  isOpen,
  onClose,
  action = 'play',
  streamName,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getTitle = () => {
    switch (action) {
      case 'play':
        return 'Sign in to Listen';
      case 'queue':
        return 'Sign in to Queue';
      case 'download':
        return 'Sign in to Download';
      case 'favorite':
        return 'Sign in to Favorite';
      case 'shownotes':
        return 'Sign in to Generate Notes';
      default:
        return 'Sign in Required';
    }
  };

  const getMessage = () => {
    const streamText = streamName ? `"${streamName}"` : 'this stream';
    switch (action) {
      case 'play':
        return `Create a free account to listen to ${streamText} and discover more amazing music.`;
      case 'queue':
        return `Sign in to add ${streamText} to your queue and build your perfect playlist.`;
      case 'download':
        return `Sign in to download ${streamText} for offline listening.`;
      case 'favorite':
        return `Sign in to save ${streamText} to your favorites.`;
      case 'shownotes':
        return `Sign in to generate AI-powered show notes for ${streamText}.`;
      default:
        return 'Please sign in to continue.';
    }
  };

  const handleSignIn = () => {
    onClose();
    navigate('/login', { state: { returnTo: window.location.pathname } });
  };

  const handleSignUp = () => {
    onClose();
    navigate('/signup', { state: { returnTo: window.location.pathname } });
  };

  return (
    <div className="login-prompt-overlay" onClick={onClose}>
      <div className="login-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <button className="login-prompt-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>

        <div className="login-prompt-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
          </svg>
        </div>

        <h2 className="login-prompt-title">{getTitle()}</h2>
        <p className="login-prompt-message">{getMessage()}</p>

        <div className="login-prompt-benefits">
          <div className="benefit-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <span>Unlimited streaming</span>
          </div>
          <div className="benefit-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <span>Create playlists & queues</span>
          </div>
          <div className="benefit-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <span>Offline downloads</span>
          </div>
        </div>

        <div className="login-prompt-actions">
          <button className="btn btn-primary login-prompt-btn" onClick={handleSignIn}>
            Sign In
          </button>
          <button className="btn btn-secondary login-prompt-btn" onClick={handleSignUp}>
            Create Account
          </button>
        </div>

        <p className="login-prompt-footer">
          It's free and takes less than a minute!
        </p>
      </div>
    </div>
  );
};

export default LoginPromptModal;
